import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { PermissionType } from 'src/types/permission';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import Detail from './Detail';
import { useNavigate } from 'react-router-dom';
import TopBlock from './TopBlock';
import {
    getPermissionDetailFetch,
    createPermissionDetailFetch,
    updatePermissionDetailFetch,
    deletePermissionFetch,
    clearPermissionDetail,
} from 'src/resources/permissionDetail/reducer';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { FormErrorsType, OnChangeType } from '../../types/global';

export type EditKeysType = 'PID' | 'DESCRIPTION';

export const EDIT_KEYS = ['PID', 'DESCRIPTION'];

const initData = {};
const PermissionDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idPermission } = useParams();

    const newPermission = !idPermission;

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.permissionDetail.data);
    const loading = useSelector((state: RootStoreType) => state.permissionDetail.loading);
    const deleted = useSelector((state: RootStoreType) => state.permissionDetail.deleted);

    const [editData, setEditData] = useState<PermissionType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionCreate = useCheckPermission('PERMISSION.CREATE');
    const permissionUpdate = useCheckPermission('PERMISSION.UPDATE');
    const disableEdit = newPermission ? !permissionCreate : !permissionUpdate;
    const permissionDelete = useCheckPermission('PERMISSION.DELETE');
    const disableDelete = newPermission || !permissionDelete;

    const backUrl = '/admin/permissions/';

    useEffect(() => {
        if (idPermission) {
            dispatch(setLocationParams({ name: translate('permissionDetail.title', { idPermission }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('permissionDetail.titleNew'), backUrl }));
        }
    }, [idPermission]);

    useEffect(() => {
        if (idPermission) {
            dispatch(getPermissionDetailFetch(idPermission));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearPermissionDetail());
        };
    }, [idPermission]);

    useEffect(() => {
        if (data) {
            setEditData(data);
        }

        if (!idPermission && data?.ID) {
            navigate(`/admin/permissions/${String(data.ID)}/`);
        }
    }, [data]);

    useEffect(() => {
        if (deleted) {
            navigate(backUrl);
        }
    }, [deleted]);

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleDeletePermission = () => {
        setConfirmParams({
            title: translate('permissionDetail.deletePermission'),
            content: translate('permissionDetail.deletePermissionConfirm'),
            action: () => dispatch(deletePermissionFetch(idPermission)),
        });
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate(newPermission ? 'permissionDetail.create' : 'permissionDetail.save'),
                content: translate(newPermission ? 'permissionDetail.createConfirm' : 'permissionDetail.saveConfirm'),
                action: () => {
                    const formatData = { ...editData };

                    if (newPermission) {
                        dispatch(createPermissionDetailFetch(formatData));
                    } else {
                        dispatch(updatePermissionDetailFetch({ idPermission, data: formatData }));
                    }
                },
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock
                data={data}
                idPermission={idPermission}
                newPermission={newPermission}
                disableEdit={disableEdit}
                disableDelete={disableDelete}
                handleSave={handleSave}
                handleDeletePermission={handleDeletePermission}
            />
            <form ref={formRef}>
                {loading && <Loading />}

                <FormBlock>
                    <Detail
                        onChange={onChange}
                        editData={editData}
                        editErrors={editErrors}
                        newPermission={newPermission}
                        disableEdit={disableEdit}
                    />
                </FormBlock>
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default PermissionDetailContainer;
